/**
 * useTreeKeyboard Hook
 *
 * Handles all keyboard interactions for the TreeGrid
 */

import { useEffect, useCallback } from "react";
import { TreeGridColumn } from "../types";

export interface UseTreeKeyboardProps {
  enabled: boolean;
  focusedCell: { rowId: string; columnKey: string } | null;
  editingRowId: string | null;
  visibleRows: any[];
  columns: TreeGridColumn[];
  rowsById: Map<string, any>;
  selectedIds: Set<string>;

  // Callbacks
  onNavigate: (direction: "up" | "down" | "left" | "right") => void;
  onStartEdit: () => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onToggleExpand: (rowId: string) => void;
  onToggleSelection: (rowId: string) => void;
  onIndent: (rowId: string) => void;
  onOutdent: (rowId: string) => void;
  onCreateRow: (parentId: string | null) => void;
  onDeleteRow: (rowId: string) => void;
}

export function useTreeKeyboard(props: UseTreeKeyboardProps) {
  const {
    enabled,
    focusedCell,
    editingRowId,
    visibleRows,
    columns,
    rowsById,
    onNavigate,
    onStartEdit,
    onCommitEdit,
    onCancelEdit,
    onToggleExpand,
    onToggleSelection,
    onIndent,
    onOutdent,
    onCreateRow,
    onDeleteRow,
  } = props;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled || !focusedCell) return;

      const { rowId, columnKey } = focusedCell;
      const row = rowsById.get(rowId);
      const column = columns.find((c) => c.key === columnKey);
      const isEditing = editingRowId !== null;

      // Don't handle if editing (except for Enter/Escape)
      if (isEditing && !["Enter", "Escape"].includes(event.key)) {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modifierKey = isMac ? event.metaKey : event.ctrlKey;

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          if (!isEditing) {
            onNavigate("up");
          }
          break;

        case "ArrowDown":
          event.preventDefault();
          if (!isEditing) {
            onNavigate("down");
          }
          break;

        case "ArrowLeft":
          if (!isEditing) {
            event.preventDefault();
            if (row?.isExpanded && row.childrenIds.length > 0) {
              onToggleExpand(rowId);
            } else {
              onNavigate("left");
            }
          }
          break;

        case "ArrowRight":
          if (!isEditing) {
            event.preventDefault();
            if (!row?.isExpanded && row?.childrenIds.length > 0) {
              onToggleExpand(rowId);
            } else {
              onNavigate("right");
            }
          }
          break;

        case "Enter":
          event.preventDefault();
          event.stopPropagation();
          if (isEditing) {
            onCommitEdit();
            // Move to next row after committing
            setTimeout(() => onNavigate("down"), 0);
          } else if (modifierKey) {
            // Ctrl/Cmd+Enter: Create new row at top level (same as Add Row button)
            onCreateRow(null);
          } else {
            // Plain Enter: Start editing current cell or move down
            if (column?.editable) {
              onStartEdit();
            } else {
              onNavigate("down");
            }
          }
          break;

        case "Escape":
          event.preventDefault();
          if (isEditing) {
            onCancelEdit();
          }
          break;

        case "Tab":
          event.preventDefault();
          if (isEditing) {
            // Commit current edit and move to next/previous cell
            onCommitEdit();
            setTimeout(() => {
              if (event.shiftKey) {
                onNavigate("left");
              } else {
                onNavigate("right");
              }
              // Auto-start editing if next column is editable
              setTimeout(() => onStartEdit(), 0);
            }, 0);
          } else {
            if (event.shiftKey) {
              onNavigate("left");
            } else {
              onNavigate("right");
            }
          }
          break;

        case "F2":
          event.preventDefault();
          if (!isEditing && column?.editable) {
            onStartEdit();
          }
          break;

        case " ":
          // Space for selection
          if (!isEditing) {
            event.preventDefault();
            onToggleSelection(rowId);
          }
          break;

        case "Delete":
        case "Backspace":
          if (!isEditing && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            onDeleteRow(rowId);
          }
          break;

        case "]":
          // Indent (make child of previous sibling)
          // Mac: Cmd+] or Ctrl+]
          // Windows: Ctrl+]
          if (!isEditing && modifierKey) {
            event.preventDefault();
            onIndent(rowId);
          }
          break;

        case "[":
          // Outdent (move to parent's level)
          // Mac: Cmd+[ or Ctrl+[
          // Windows: Ctrl+[
          if (!isEditing && modifierKey) {
            event.preventDefault();
            onOutdent(rowId);
          }
          break;

        default:
          break;
      }
    },
    [
      enabled,
      focusedCell,
      editingRowId,
      rowsById,
      columns,
      onNavigate,
      onStartEdit,
      onCommitEdit,
      onCancelEdit,
      onToggleExpand,
      onToggleSelection,
      onIndent,
      onOutdent,
      onCreateRow,
      onDeleteRow,
    ]
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled, handleKeyDown]);
}
